import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, X } from 'lucide-react'
import { Button, Input } from '@ludus/ui'

/** The route's rule, mirrored so the message arrives before the request does. */
const EXTENSION = /^\d{1,8}$/

interface ExtensionsEditorProps {
  extensions: string[]
  disabled: boolean
  onChange: (extensions: string[]) => Promise<boolean>
}

export function ExtensionsEditor({ extensions, disabled, onChange }: ExtensionsEditorProps) {
  const { t } = useTranslation()
  const [draft, setDraft] = useState('')
  const [invalid, setInvalid] = useState(false)

  const add = async (event: FormEvent) => {
    event.preventDefault()
    const digits = draft.trim()
    if (!EXTENSION.test(digits)) {
      setInvalid(true)
      return
    }
    setInvalid(false)
    if (extensions.includes(digits) || (await onChange([...extensions, digits]))) setDraft('')
  }

  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs text-muted">{t('pages.managers.extensions.hint')}</p>
      {extensions.length > 0 && (
        <ul className="flex flex-wrap gap-1.5">
          {extensions.map((extension) => (
            <li
              key={extension}
              className="inline-flex items-center gap-1 rounded-md border border-border bg-background-secondary px-2 py-0.5 font-mono text-xs text-foreground"
            >
              {extension}
              <button
                type="button"
                disabled={disabled}
                aria-label={t('pages.managers.extensions.remove', { extension })}
                className="rounded text-muted hover:text-foreground disabled:opacity-50"
                onClick={() => void onChange(extensions.filter((e) => e !== extension))}
              >
                <X className="h-3 w-3" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <form className="flex flex-wrap items-center gap-2" onSubmit={(event) => void add(event)}>
        <Input
          value={draft}
          inputMode="numeric"
          disabled={disabled}
          aria-invalid={invalid}
          aria-label={t('pages.managers.extensions.add')}
          placeholder={t('pages.managers.extensions.placeholder')}
          className="h-8 w-32 font-mono"
          onChange={(e) => {
            setDraft(e.target.value)
            setInvalid(false)
          }}
        />
        <Button
          type="submit"
          variant="outline"
          size="sm"
          disabled={disabled || draft.trim() === ''}
        >
          <Plus className="h-3.5 w-3.5" aria-hidden="true" />
          {t('pages.managers.extensions.add')}
        </Button>
        {invalid && (
          <span role="alert" className="basis-full text-[11px] font-medium text-toast-error-text">
            {t('pages.managers.extensions.invalid')}
          </span>
        )}
      </form>
    </div>
  )
}
